import { html, nothing, unsafeCSS, type CSSResultGroup, type PropertyValues } from 'lit';
import { live } from 'lit/directives/live.js';
import { customElement, property, state } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import '../button/lib-button';
import '../calendar/lib-calendar';
import '../combobox/lib-combobox';
import '../combobox/lib-combobox-option';
import '../flyout/lib-flyout';
import '../icon/lib-icon';
import '../textbox/lib-textbox';
import '../time-picker/lib-time-picker';
import css from './lib-time-range-picker.css';

type End = 'from' | 'to';
interface Preset { value: string; text: string; }
interface Part { token?: string; literal?: string; }
interface ValueElement extends HTMLElement { value: string; }

const icons = {
  calendar: new URL('../wago-icons-svg/calendar.svg', import.meta.url).href,
  chevron: new URL('../wago-icons-svg/btn-chevron-down.svg', import.meta.url).href,
};
const valuePattern = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
const pad = (value: number, length = 2): string => String(value).padStart(length, '0');
const daysInMonth = (year: number, month: number): number => {
  if (month === 2) return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
};
const fill = (template: string, ...values: string[]): string =>
  values.reduce((text, value, index) => text.replace(`{${index}}`, value), template);
const later = (first: string, second: string): string => {
  if (!first) return second;
  if (!second) return first;
  return first > second ? first : second;
};
const earlier = (first: string, second: string): string => {
  if (!first) return second;
  if (!second) return first;
  return first < second ? first : second;
};
const minuteOfDay = (value: string): number => Number(value.slice(11, 13)) * 60 + Number(value.slice(14, 16));

/** Splits a format into the supported tokens (yyyy, MM, M, dd, d, HH, H, mm) and literal characters. */
function tokenize(format: string): Part[] {
  const parts: Part[] = [];
  for (let index = 0; index < format.length;) {
    const char = format[index];
    if (!/[A-Za-z]/.test(char)) { parts.push({ literal: char }); index++; continue; }
    let end = index;
    while (format[end] === char) end++;
    parts.push({ token: format.slice(index, end) });
    index = end;
  }
  return parts;
}

/** Formats a `yyyy-MM-ddTHH:mm` value with the given format. */
export function formatValue(value: string, format: string): string {
  const match = valuePattern.exec(value);
  if (!match) return '';
  const [year, month, day, hour, minute] = match.slice(1).map(Number);
  const tokens: Record<string, string> = {
    yyyy: pad(year, 4), MM: pad(month), M: String(month), dd: pad(day), d: String(day), HH: pad(hour), H: String(hour), mm: pad(minute),
  };
  return tokenize(format).map(part => part.literal ?? tokens[part.token!] ?? part.token).join('');
}

/** Parses text written in the given format into a `yyyy-MM-ddTHH:mm` value, or returns null. */
export function parseValue(text: string, format: string): string | null {
  const parts = tokenize(format);
  const patterns: Record<string, string> = { yyyy: String.raw`(\d{4})`, MM: String.raw`(\d{2})`, dd: String.raw`(\d{2})`, HH: String.raw`(\d{2})`, mm: String.raw`(\d{2})`, M: String.raw`(\d{1,2})`, d: String.raw`(\d{1,2})`, H: String.raw`(\d{1,2})` };
  const order: string[] = [];
  let source = '';
  for (const part of parts) {
    if (part.literal !== undefined) { source += part.literal.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`); continue; }
    const pattern = patterns[part.token!];
    if (!pattern) return null;
    source += pattern;
    order.push(part.token![0]);
  }
  const match = new RegExp(`^${source}$`).exec(text.trim());
  if (!match) return null;
  const field = (letter: string): number => Number(match[order.indexOf(letter) + 1]);
  const year = field('y'), month = field('M'), day = field('d'), hour = field('H'), minute = field('m');
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month) || hour > 23 || minute > 59) return null;
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`;
}

/**
 * A time range picker with one combined date-and-time field per end. Each field opens a popup with
 * a calendar and a time picker side by side; the `dropdown` mode shows both fields and the pickers
 * of the active end in one panel. Values are `yyyy-MM-ddTHH:mm` in the caller's time zone.
 *
 * Popup selections dispatch `lib-time-range-picker-change` with `{ from, to, preset }` only on Apply;
 * typed edits and quick-navigation choices dispatch immediately.
 * a quick-navigation choice reports the chosen `preset` and leaves the range to the caller.
 * `lib-time-range-picker-input` reports the localized error of an invalid draft (empty when none).
 */
@customElement('lib-time-range-picker')
export class LibTimeRangePicker extends LibElement {
  protected override get hostFocusSelector(): string {
    return '.lib-time-range-picker-summary, .lib-time-range-picker-field > lib-textbox';
  }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];
  private revision = 0;
  static override get observedAttributes(): string[] { return [...super.observedAttributes, 'server-state']; }
  override attributeChangedCallback(name: string, previous: string | null, value: string | null): void {
    if (name === 'server-state') {
      if (value !== null) {
        const state = JSON.parse(value) as Record<string, string> & { revision: number };
        if (state.revision >= this.revision) {
          this.from = state.from;
          this.to = state.to;
          this.preset = state.preset;
        }
      }
      return;
    }
    if (['from', 'to', 'preset'].includes(name) && this.hasAttribute('server-state')) return;
    super.attributeChangedCallback(name, previous, value);
  }
  protected override createRenderRoot(): HTMLElement | DocumentFragment {
    const root = super.createRenderRoot();
    // Capture before nested controls stop propagation; rendering here could replace native input before its handler reads it.
    root.addEventListener('input', () => { this.revision++; }, true);
    // A partial option selection changes the nested time draft without a native change.
    const childRevisions = new WeakMap<EventTarget, number>();
    root.addEventListener('lib-time-picker-input', event => {
      const revision = (event as CustomEvent<{ revision: number }>).detail.revision;
      if (event.target && revision > (childRevisions.get(event.target) ?? 0)) {
        childRevisions.set(event.target, revision);
        this.revision++;
        this.requestUpdate();
      }
    }, true);
    return root;
  }
  @property() from = '';
  @property() to = '';
  @property() min = '';
  @property() max = '';
  @property() format = 'yyyy-MM-dd HH:mm';
  @property() mode = 'standard';
  @property() surface = 'white';
  @property() preset = '-1';
  @property({ attribute: false }) presets: Preset[] = [];
  @property({ attribute: false }) months: string[] = [];
  @property({ attribute: false }) weekdays: string[] = [];
  @property({ attribute: 'from-label' }) fromLabel = '';
  @property({ attribute: 'to-label' }) toLabel = '';
  @property({ attribute: 'date-range-label' }) dateRangeLabel = '';
  @property({ attribute: 'quick-navigation-label' }) quickNavigationLabel = '';
  @property({ attribute: 'apply-label' }) applyLabel = '';
  @property({ attribute: 'open-from-label' }) openFromLabel = '';
  @property({ attribute: 'open-to-label' }) openToLabel = '';
  @property({ attribute: 'invalid-format-message' }) invalidFormatMessage = '';
  @property({ attribute: 'from-after-to-message' }) fromAfterToMessage = '';
  @property({ attribute: 'out-of-range-message' }) outOfRangeMessage = '';
  @property({ attribute: 'before-min-message' }) beforeMinMessage = '';
  @property({ attribute: 'after-max-message' }) afterMaxMessage = '';
  @property({ attribute: 'calendar-label' }) calendarLabel = '';
  @property({ attribute: 'today-date' }) todayDate = '';
  @property({ attribute: 'today-label' }) todayLabel = '';
  @property({ attribute: 'previous-month-label' }) previousMonthLabel = '';
  @property({ attribute: 'next-month-label' }) nextMonthLabel = '';
  @property({ attribute: 'previous-year-label' }) previousYearLabel = '';
  @property({ attribute: 'next-year-label' }) nextYearLabel = '';
  @property({ attribute: 'time-label' }) timeLabel = '';
  @property({ attribute: 'hours-label' }) hoursLabel = '';
  @property({ attribute: 'minutes-label' }) minutesLabel = '';
  @property({ attribute: 'invalid-hour-message' }) invalidHourMessage = '';
  @property({ attribute: 'invalid-minute-message' }) invalidMinuteMessage = '';
  @property({ attribute: 'select-hour-message' }) selectHourMessage = '';
  @property({ attribute: 'select-minute-message' }) selectMinuteMessage = '';
  @property({ attribute: 'unfinished-message' }) unfinishedMessage = '';
  @property({ attribute: 'time-out-of-range-message' }) timeOutOfRangeMessage = '';
  @property({ attribute: 'time-before-min-message' }) timeBeforeMinMessage = '';
  @property({ attribute: 'time-after-max-message' }) timeAfterMaxMessage = '';
  @property() culture = 'en-US';
  @property({ type: Number, attribute: 'first-day' }) firstDay = 0;
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) error = false;
  @property() message = '';
  @property({ attribute: 'aria-label' }) override ariaLabel: string | null = null;
  @state() private text: Record<End, string> = { from: '', to: '' };
  @state() private fieldError: Record<End, string> = { from: '', to: '' };
  @state() private open: End | 'panel' | '' = '';
  @state() private active: End = 'from';
  @state() private draft: Record<End, string> | null = null;
  @state() private pickerError = '';
  private reportedRevision = -1;
  private reported = '';

  private t(key: keyof typeof textProperties, fallback: string): string { return this[textProperties[key]] || fallback; }
  private display(value: string): string { return formatValue(value, this.format); }
  private selected(end: End): string { return this.draft?.[end] ?? this[end]; }
  private lower(end: End): string { return end === 'from' ? this.min : later(this.min, this.selected('from')); }
  private upper(end: End): string { return end === 'to' ? this.max : earlier(this.max, this.selected('to')); }

  protected override willUpdate(changed: PropertyValues<this>): void {
    if (changed.has('from') || changed.has('to') || changed.has('preset') || changed.has('min') || changed.has('max')) {
      this.resetDraft();
    }
    for (const end of ['from', 'to'] as End[]) {
      if (changed.has(end) || changed.has('format')) {
        this.text = { ...this.text, [end]: this.display(this[end]) };
        if (changed.has(end)) this.fieldError = { ...this.fieldError, [end]: '' };
      }
    }
    if ((changed.has('disabled') && this.disabled) || changed.has('mode')) this.dismiss();
  }

  protected override updated(): void {
    const error = this.fieldError.from || this.fieldError.to;
    if (error === this.reported && this.reportedRevision === this.revision) return;
    this.reportedRevision = this.revision;
    this.reported = error;
    this.emit('lib-time-range-picker-input', { error, revision: this.revision });
  }

  private validate(end: End, value: string, draft = false): string {
    if (this.min && this.max && (value < this.min || value > this.max))
      return fill(this.t('outOfRange', 'Choose a time between {0} and {1}.'), this.display(this.min), this.display(this.max));
    if (this.min && value < this.min) return fill(this.t('beforeMin', 'Choose {0} or later.'), this.display(this.min));
    if (this.max && value > this.max) return fill(this.t('afterMax', 'Choose {0} or earlier.'), this.display(this.max));
    const from = draft ? this.selected('from') : this.from, to = draft ? this.selected('to') : this.to;
    if (end === 'from' ? to && value > to : from && value < from)
      return this.t('fromAfterTo', 'The start must not be later than the end.');
    return '';
  }

  private clamp(end: End, value: string): string {
    const lower = this.lower(end), upper = this.upper(end);
    if (lower && value < lower) return lower;
    if (upper && value > upper) return upper;
    return value;
  }

  private commit(end: End, value: string, draft = false): void {
    const error = this.validate(end, value, draft);
    this.fieldError = { ...this.fieldError, [end]: error };
    if (error) return;
    this.text = { ...this.text, [end]: this.display(value) };
    if (draft) {
      this.draft = { from: this.selected('from'), to: this.selected('to'), [end]: value };
      return;
    }
    this.resetDraft();
    this.text = { ...this.text, [end]: this.display(value) };
    if (value === this[end]) return;
    this[end] = value;
    this.preset = '-1';
    this.emit('lib-time-range-picker-change', { from: this.from, to: this.to, preset: '-1', revision: this.revision });
  }

  private textChange(event: Event, end: End): void {
    event.stopPropagation();
    if (this.disabled) return;
    const raw = (event.target as ValueElement).value;
    this.text = { ...this.text, [end]: raw };
    const value = parseValue(raw, this.format);
    if (value === null) {
      this.fieldError = { ...this.fieldError, [end]: fill(this.t('invalidFormat', 'Enter the date and time as {0}.'), this.format) };
      return;
    }
    this.commit(end, value);
  }

  private dateChange(event: Event, end: End): void {
    this.revision++;
    event.stopPropagation();
    const date = (event.target as ValueElement).value;
    if (this.disabled || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    this.commit(end, this.clamp(end, `${date}T${this.selected(end).slice(11) || '00:00'}`), true);
  }

  private timeChange(event: Event, end: End): void {
    this.revision++;
    event.stopPropagation();
    const time = (event.target as ValueElement).value;
    if (this.disabled || !/^\d{2}:\d{2}$/.test(time) || !this.selected(end)) return;
    this.commit(end, this.clamp(end, `${this.selected(end).slice(0, 10)}T${time}`), true);
  }

  private presetChange(event: Event): void {
    this.revision++;
    event.stopPropagation();
    if (this.disabled) return;
    this.resetDraft();
    this.preset = (event.target as ValueElement).value;
    this.emit('lib-time-range-picker-change', { from: this.from, to: this.to, preset: this.preset, revision: this.revision });
  }

  private toggle(target: End | 'panel'): void {
    if (this.disabled) return;
    const wasOpen = this.open === target;
    this.dismiss();
    this.open = wasOpen ? '' : target;
    this.resetDraft();
  }

  private resetDraft(): void {
    this.draft = this.open ? { from: this.from, to: this.to } : null;
    this.text = { from: this.display(this.from), to: this.display(this.to) };
    this.fieldError = { from: '', to: '' };
    this.pickerError = '';
  }

  private dismiss(): void {
    this.revision++;
    this.open = '';
    this.resetDraft();
  }

  private apply(): void {
    if (this.disabled || this.pickerError || this.fieldError.from || this.fieldError.to || !this.draft) return;
    const { from, to } = this.draft;
    if (this.validate('from', from, true) || this.validate('to', to, true)) return;
    const changed = from !== this.from || to !== this.to;
    this.from = from;
    this.to = to;
    if (changed) this.preset = '-1';
    this.dismiss();
    if (changed) this.emit('lib-time-range-picker-change', { from, to, preset: '-1', revision: this.revision });
  }

  private actions() {
    return html`<div class="lib-time-range-picker-actions">
      <lib-button variant="primary" ?disabled=${this.disabled || !!this.pickerError || !!this.fieldError.from || !!this.fieldError.to}
        @click=${this.apply}>${this.t('apply', 'Apply')}</lib-button>
    </div>`;
  }

  private fieldKeydown(event: KeyboardEvent, end: End, inline: boolean): void {
    if (inline || event.key !== 'ArrowDown' || !event.altKey || (event.composedPath()[0] as Element).tagName !== 'INPUT') return;
    event.preventDefault();
    if (this.open !== end) this.toggle(end);
  }

  private stop(event: Event): void { event.stopPropagation(); }

  private timeRangeMessage(lower: number | null, upper: number | null): string {
    const time = (minutes: number): string => `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
    if (lower !== null && upper !== null) return fill(this.t('timeOutOfRange', 'Choose a time between {0} and {1}.'), time(lower), time(upper));
    if (lower !== null) return fill(this.t('timeBeforeMin', 'Choose {0} or later.'), time(lower));
    if (upper !== null) return fill(this.t('timeAfterMax', 'Choose {0} or earlier.'), time(upper));
    return '';
  }

  private pickers(end: End) {
    const value = this.selected(end), date = value.slice(0, 10), lower = this.lower(end), upper = this.upper(end);
    const minMinute = date.length > 0 && lower.slice(0, 10) === date ? minuteOfDay(lower) : null;
    const maxMinute = date.length > 0 && upper.slice(0, 10) === date ? minuteOfDay(upper) : null;
    return html`<div class="lib-time-range-picker-pickers">
      <lib-calendar today-date=${this.todayDate} .selectedDate=${live(date)} .minDate=${lower.slice(0, 10)} .maxDate=${upper.slice(0, 10)}
        .months=${this.months} .weekdays=${this.weekdays} culture=${this.culture} first-day=${this.firstDay}
        ?disabled=${this.disabled} grid-label=${this.t('calendar', 'Calendar')} today-label=${this.t('today', 'Today')}
        previous-month-label=${this.t('previousMonth', 'Previous month')} next-month-label=${this.t('nextMonth', 'Next month')}
        previous-year-label=${this.t('previousYear', 'Previous year')} next-year-label=${this.t('nextYear', 'Next year')}
        @change=${(event: Event) => this.dateChange(event, end)} @input=${this.stop}></lib-calendar>
      <div class="lib-time-range-picker-divider" aria-hidden="true"></div>
      <lib-time-picker .value=${live(value.slice(11))} .minMinute=${minMinute} .maxMinute=${maxMinute} ?disabled=${this.disabled}
        group-label=${this.t('time', 'Time')} hours-label=${this.t('hours', 'Hours')} minutes-label=${this.t('minutes', 'Minutes')}
        invalid-hour-message=${this.t('invalidHour', 'Enter an hour from 00 to 23.')}
        invalid-minute-message=${this.t('invalidMinute', 'Enter minutes from 00 to 59.')}
        select-hour-message=${this.t('selectHour', 'Select an hour.')} select-minute-message=${this.t('selectMinute', 'Select minutes.')}
        unfinished-message=${this.t('unfinished', 'Finish entering the time.')} range-message=${this.timeRangeMessage(minMinute, maxMinute)}
        @change=${(event: Event) => this.timeChange(event, end)}
        @lib-time-picker-input=${(event: CustomEvent<{ error: string }>) => { event.stopPropagation(); this.pickerError = event.detail.error; }}></lib-time-picker>
    </div>`;
  }

  private inlineField(end: End) {
    return this.field(end, () => { this.active = end; }, 'inline');
  }

  private popupField(end: End) {
    return this.field(end, () => this.toggle(end), 'popup');
  }

  private field(end: End, activate: () => void, presentation: 'inline' | 'popup') {
    const inline = presentation === 'inline';
    const popup = `${end}-popup`;
    const label = end === 'from' ? this.t('from', 'From') : this.t('to', 'To');
    const openLabel = end === 'from' ? this.t('openFrom', 'Choose start date and time') : this.t('openTo', 'Choose end date and time');
    const active = inline && this.active === end;
    return html`<div class="lib-time-range-picker-field ${active ? 'lib-time-range-picker-field--active' : ''}" data-end=${end}>
      <lib-textbox surface=${inline ? 'white' : this.surface} headline=${label} placeholder=${this.format} .value=${live(this.text[end])} bind-mode="onlostfocus"
        maxlength="40" ?disabled=${this.disabled} ?error=${this.error || !!this.fieldError[end]} message=${this.fieldError[end]}
        data-lib-flyout-anchor=${inline ? 'panel-popup' : popup}
        @change=${(event: Event) => this.textChange(event, end)} @input=${this.stop}
        @keydown=${(event: KeyboardEvent) => this.fieldKeydown(event, end, inline)}
        @focusin=${() => { if (inline) this.active = end; }}>
        <button slot="icon" type="button" class="lib-time-range-picker-trigger ${active ? 'lib-time-range-picker-trigger--active' : ''}"
          aria-label=${openLabel} title=${openLabel} ?disabled=${this.disabled}
          aria-haspopup=${inline ? nothing : 'dialog'} aria-expanded=${inline ? nothing : String(this.open === end)}
          aria-pressed=${inline ? String(active) : nothing} aria-controls=${inline ? 'panel-pickers' : popup}
          @click=${activate}>
          <lib-icon icon-url=${icons.calendar}></lib-icon>
        </button>
      </lib-textbox>
      ${inline ? nothing : html`<lib-flyout id=${popup} alignment="start" custom-surface ?open=${this.open === end}
        @lib-flyout-dismiss=${() => { if (this.open === end) this.dismiss(); }}>
        <div class="lib-time-range-picker-surface" role="dialog" aria-label=${label}>${this.pickers(end)}${this.actions()}</div>
      </lib-flyout>`}
    </div>`;
  }

  private quickNavigation(surface = this.surface) {
    if (!this.presets?.length) return nothing;
    return html`<lib-combobox surface=${surface} class="lib-time-range-picker-presets" headline=${this.t('quickNavigation', 'Quick navigation')}
      .value=${live(this.preset)} ?disabled=${this.disabled} @change=${this.presetChange}>
      ${this.presets.map(preset => html`<lib-combobox-option value=${preset.value}>${preset.text}</lib-combobox-option>`)}
    </lib-combobox>`;
  }

  private dropdown() {
    const open = this.open === 'panel';
    const invalid = this.error || !!this.fieldError.from || !!this.fieldError.to;
    const activeLabel = this.active === 'from' ? this.t('from', 'From') : this.t('to', 'To');
    return html`<span id="summary-label" class="lib-time-range-picker-label">${this.t('dateRange', 'Date range')}</span>
      <div class="lib-time-range-picker-anchor">
        <button type="button" class="lib-time-range-picker-summary ${invalid ? 'lib-time-range-picker-summary--error' : ''}"
          aria-labelledby="summary-label summary-value" aria-haspopup="dialog" aria-expanded=${String(open)}
          aria-controls="panel-popup" aria-invalid=${invalid ? 'true' : nothing} ?disabled=${this.disabled}
          @click=${() => this.toggle('panel')}>
          <span id="summary-value" class="lib-time-range-picker-summary-value">${this.display(this.from)} – ${this.display(this.to)}</span>
          <lib-icon class="lib-time-range-picker-chevron ${open ? 'lib-time-range-picker-chevron--open' : ''}" icon-url=${icons.chevron}></lib-icon>
        </button>
        <lib-flyout id="panel-popup" alignment="start" custom-surface ?open=${open}
          @lib-flyout-dismiss=${() => { if (this.open === 'panel') this.dismiss(); }}>
          <div class="lib-time-range-picker-surface lib-time-range-picker-panel" role="dialog" aria-label=${this.t('dateRange', 'Date range')}>
            ${this.quickNavigation('white')}
            <div class="lib-time-range-picker-fields">${this.inlineField('from')}${this.inlineField('to')}</div>
            <div id="panel-pickers" role="group" aria-label=${activeLabel}>${this.pickers(this.active)}</div>
            ${this.actions()}
          </div>
        </lib-flyout>
      </div>`;
  }

  override render() {
    const mode = ['simple', 'dropdown'].includes(this.mode) ? this.mode : 'standard';
    const showMessage = !!this.message && !this.fieldError.from && !this.fieldError.to;
    const quickNavigation = mode === 'standard' ? this.quickNavigation() : nothing;
    return html`<div class="lib-time-range-picker lib-time-range-picker--${mode} lib-time-range-picker-surface-${this.surface} ${this.disabled ? 'lib-time-range-picker--disabled' : ''}"
      role="group" aria-label=${this.ariaLabel || this.t('dateRange', 'Date range')} aria-describedby=${showMessage ? 'message' : nothing}>
      ${mode === 'dropdown' ? this.dropdown() : html`<div class="lib-time-range-picker-fields">
        ${this.popupField('from')}${this.popupField('to')}${quickNavigation}
      </div>`}
      ${showMessage ? html`<div id="message" class="lib-time-range-picker-message">${this.message}</div>` : nothing}
    </div>`;
  }
}
declare global { interface HTMLElementTagNameMap { 'lib-time-range-picker': LibTimeRangePicker; } }

const textProperties = {
  from: 'fromLabel', to: 'toLabel', dateRange: 'dateRangeLabel', quickNavigation: 'quickNavigationLabel',
  apply: 'applyLabel', openFrom: 'openFromLabel', openTo: 'openToLabel', invalidFormat: 'invalidFormatMessage',
  fromAfterTo: 'fromAfterToMessage', outOfRange: 'outOfRangeMessage', beforeMin: 'beforeMinMessage', afterMax: 'afterMaxMessage',
  calendar: 'calendarLabel', today: 'todayLabel', previousMonth: 'previousMonthLabel', nextMonth: 'nextMonthLabel',
  previousYear: 'previousYearLabel', nextYear: 'nextYearLabel', time: 'timeLabel', hours: 'hoursLabel', minutes: 'minutesLabel',
  invalidHour: 'invalidHourMessage', invalidMinute: 'invalidMinuteMessage', selectHour: 'selectHourMessage',
  selectMinute: 'selectMinuteMessage', unfinished: 'unfinishedMessage', timeOutOfRange: 'timeOutOfRangeMessage',
  timeBeforeMin: 'timeBeforeMinMessage', timeAfterMax: 'timeAfterMaxMessage',
} as const;

/** Sets structured Blazor data directly on the element, without JSON attributes. */
export function setTimeRangePickerData(element: LibTimeRangePicker | null, presets: Preset[], months: string[], weekdays: string[]): void {
  if (!element) return;
  element.presets = presets;
  element.months = months;
  element.weekdays = weekdays;
}
